/**
 * InboundTaxDecisionPanel — manuelle Steuerkontierung eines Eingangsbelegs
 * (architecture-eingang-steuerkontierung.md §5.4/§6). Inline-Panel (kein Modal,
 * Muster `KontierungVorschlagPanel`).
 *
 * Zwei Zustände, abgeleitet aus `resolveEffectiveTaxScheme(doc)`:
 *  - aktive, nicht-stale Entscheidung ⇒ Bestätigungs-Ansicht (festgelegter Wert,
 *    dauerhaft sichtbarer Auto-Wert, Zeitpunkt, Disclaimer, „Kontierung aufheben").
 *  - sonst ⇒ Trigger „Kontierung festlegen"/„Kontierung bestätigen", der ein
 *    Inline-Formular öffnet (Kategorie-Select, optionaler Steuersatz-Hinweis,
 *    Begründung, J-2-Pflicht-Warnung, Submit + Disclaimer).
 *
 * Owner-only v1 (J-3): das Panel wird von der Seite NUR gerendert, wenn
 * `canDecideTax` gilt. Alle harten Checks (Flag, Owner, Zustand, J-2-Note-Pflicht)
 * laufen zusätzlich serverseitig — dies ist die UX-Spiegelung.
 *
 * @module InboundTaxDecisionPanel
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IconWarning, IconSpinner, IconCheck, IconUndo } from '../../icons';
import type {
  InboundDocument,
  InboundTaxDecision,
  InboundTaxScheme,
} from '../../types/inboundTypes';
import {
  resolveEffectiveTaxScheme,
  noteRequiredFor,
  INBOUND_TAX_DECISION_SELECTABLE,
} from '../../utils/inboundTaxDecision';
import {
  setInboundTaxDecision,
  type SelectableTaxScheme,
} from '../../api/inboundApi';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';

interface InboundTaxDecisionPanelProps {
  doc: InboundDocument;
  /** Nach erfolgreichem Write: der neue Summary-Stand (`null` nach Aufheben). */
  onDecided: (taxDecision: InboundTaxDecision | null) => void;
}

/** Sentinel-Wert des Selects: den (nicht wählbaren) Auto-Wert bestätigen. */
const CONFIRM_AUTO = '__confirm_auto__';

/** Formatiert einen Firestore-Timestamp fail-safe in ein lokales Datum. */
function fmtDate(
  ts: InboundTaxDecision['decidedAt'],
  lang: string,
): string {
  if (!ts || typeof ts.toDate !== 'function') return '–';
  try {
    return new Intl.DateTimeFormat(lang || 'de-DE', { dateStyle: 'medium' }).format(
      ts.toDate(),
    );
  } catch {
    return '–';
  }
}

const InboundTaxDecisionPanel: React.FC<InboundTaxDecisionPanelProps> = ({
  doc,
  onDecided,
}) => {
  const { t, i18n } = useTranslation('posteingang');

  const effective = useMemo(() => resolveEffectiveTaxScheme(doc), [doc]);
  const auto = effective.scheme; // im Auto-/stale-Zustand == doc.taxScheme ?? 'unknown'
  const hasActiveDecision =
    effective.source === 'manual' && effective.decision !== null;

  /** Label eines Schemes (i18n, fallback = roher Schlüssel). */
  const schemeLabel = useCallback(
    (scheme: InboundTaxScheme): string =>
      t(`taxDecision.scheme.${scheme}`, scheme),
    [t],
  );

  // ── Auto-Wert kann bestätigt, aber nicht als Override-Ziel gewählt werden
  //    (mixed / reverse_charge_mixed) → eigene „Auto bestätigen"-Option. ────────
  const autoConfirmableNotSelectable =
    auto === 'mixed' || auto === 'reverse_charge_mixed';

  const initialSelected = useMemo<string>(() => {
    if ((INBOUND_TAX_DECISION_SELECTABLE as readonly string[]).includes(auto)) {
      return auto;
    }
    if (autoConfirmableNotSelectable) return CONFIRM_AUTO;
    return 'standard'; // unknown → erster wählbarer Wert (Override).
  }, [auto, autoConfirmableNotSelectable]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>(initialSelected);
  const [note, setNote] = useState('');
  const [rateHint, setRateHint] = useState<'' | '0' | '7' | '19'>('');
  const [saving, setSaving] = useState(false);

  // Ziel-Scheme (für J-2-Regel): confirm-auto behält den Auto-Wert.
  const targetScheme: InboundTaxScheme =
    selected === CONFIRM_AUTO ? auto : (selected as InboundTaxScheme);
  const noteRequired = noteRequiredFor(auto, targetScheme);
  const noteMissing = noteRequired && note.trim().length === 0;

  const handleSubmit = useCallback(async () => {
    if (saving || noteMissing) return;
    setSaving(true);
    try {
      const trimmed = note.trim();
      const rate =
        selected === 'standard' && rateHint !== ''
          ? (Number(rateHint) as 0 | 7 | 19)
          : null;
      const { taxDecision } =
        selected === CONFIRM_AUTO
          ? await setInboundTaxDecision({
              docId: doc.id,
              action: 'confirm',
              ...(trimmed ? { note: trimmed } : {}),
              taxRateHint: rate,
            })
          : await setInboundTaxDecision({
              docId: doc.id,
              action: 'override',
              scheme: selected as SelectableTaxScheme,
              ...(trimmed ? { note: trimmed } : {}),
              taxRateHint: rate,
            });
      setOpen(false);
      setNote('');
      onDecided(taxDecision);
    } catch (err: unknown) {
      logger.error(err, 'setInboundTaxDecision (set/override) failed');
      toast.error(mapErrorToUserMessage(err, t, { fallbackKey: 'taxDecision.error' }));
    } finally {
      setSaving(false);
    }
  }, [saving, noteMissing, note, selected, rateHint, doc.id, onDecided, t]);

  const handleRevoke = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { taxDecision } = await setInboundTaxDecision({
        docId: doc.id,
        action: 'revoke',
      });
      onDecided(taxDecision);
    } catch (err: unknown) {
      logger.error(err, 'setInboundTaxDecision (revoke) failed');
      toast.error(mapErrorToUserMessage(err, t, { fallbackKey: 'taxDecision.error' }));
    } finally {
      setSaving(false);
    }
  }, [saving, doc.id, onDecided, t]);

  // ── Bestätigungs-Ansicht (aktive Entscheidung) ─────────────────────────────
  if (hasActiveDecision && effective.decision) {
    const d = effective.decision;
    const author = t('taxDecision.box.author', 'Sie');
    return (
      <div
        className="mt-3 bg-[rgb(var(--surface-base-rgb)/0.4)] border border-hairline rounded-lg p-3 text-sm"
        data-testid="inbound-tax-decision-box"
      >
        <div className="flex items-start gap-2">
          <span className="text-info mt-0.5">
            <IconCheck size={14} />
          </span>
          <div className="flex-1">
            <p className="font-semibold text-primary">
              {t('taxDecision.box.decided', { scheme: schemeLabel(d.scheme) })}
            </p>
            <p className="text-secondary">
              {t('taxDecision.box.auto', {
                scheme: schemeLabel(d.previousAutoScheme),
              })}
            </p>
            <p className="text-faint">
              {t('taxDecision.box.meta', {
                date: fmtDate(d.decidedAt, i18n.language),
                author,
              })}
            </p>
            <p className="text-faint mt-2">
              {t('taxDecision.box.disclaimer')}
            </p>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={saving}
              className="mt-2 inline-flex items-center gap-2 text-xs text-muted hover:text-primary disabled:opacity-60"
            >
              {saving ? (
                <IconSpinner className="animate-spin" />
              ) : (
                <IconUndo size={12} />
              )}
              {t('taxDecision.action.revoke', 'Kontierung aufheben')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Trigger (nicht geöffnet) ───────────────────────────────────────────────
  const triggerLabel =
    auto === 'unknown' || auto === 'mixed'
      ? t('taxDecision.action.set', 'Kontierung festlegen')
      : t('taxDecision.action.confirm', 'Kontierung bestätigen');

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-2 text-sm bg-surface-inset hover:bg-surface-hover text-primary px-3 py-1.5 rounded-lg font-medium transition-colors"
        data-testid="inbound-tax-decision-trigger"
      >
        {triggerLabel}
      </button>
    );
  }

  // ── Eingabe-Formular ───────────────────────────────────────────────────────
  const selectOptions: { value: string; label: string }[] = [];
  if (autoConfirmableNotSelectable) {
    selectOptions.push({ value: CONFIRM_AUTO, label: schemeLabel(auto) });
  }
  for (const s of INBOUND_TAX_DECISION_SELECTABLE) {
    selectOptions.push({ value: s, label: schemeLabel(s) });
  }

  return (
    <div
      className="mt-3 bg-surface-inset border border-hairline rounded-lg p-3 text-sm space-y-3"
      data-testid="inbound-tax-decision-form"
    >
      {/* Auto-Wert bleibt dauerhaft sichtbar (J-2 / §146 AO). */}
      <p className="text-faint">
        {t('taxDecision.box.auto', { scheme: schemeLabel(auto) })}
      </p>

      <div>
        <label className="block text-faint mb-1" htmlFor="inbound-tax-scheme">
          {t('taxDecision.selectLabel', 'Steuerkategorie')}
        </label>
        <select
          id="inbound-tax-scheme"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-primary"
        >
          {selectOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Steuersatz-Hinweis — nur bei 'standard' aktiv; reine Anzeige (E-2). */}
      {selected === 'standard' && (
        <div>
          <label className="block text-faint mb-1" htmlFor="inbound-tax-rate">
            {t('taxDecision.rateHintLabel', 'Steuersatz-Hinweis')}
          </label>
          <select
            id="inbound-tax-rate"
            value={rateHint}
            onChange={(e) =>
              setRateHint(e.target.value as '' | '0' | '7' | '19')
            }
            className="w-full bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-primary"
          >
            <option value="">{t('taxDecision.rateHintNone', 'Kein Hinweis')}</option>
            <option value="0">0 %</option>
            <option value="7">7 %</option>
            <option value="19">19 %</option>
          </select>
          <p className="text-faint text-xs mt-1">
            {t('taxDecision.rateHintNote')}
          </p>
        </div>
      )}

      {/* J-2: stehende (nicht wegklickbare) Pflicht-Warnung beim Weg-Override
          von §13b/IG. Auto-Wert oben bleibt sichtbar; Begründung erzwungen. */}
      {noteRequired && (
        <div
          className="flex items-start gap-2 bg-warn/10 border border-warn/30 rounded-lg p-2 text-ink"
          role="alert"
          data-testid="inbound-tax-decision-warning-13b"
        >
          <span className="text-warn mt-0.5">
            <IconWarning size={14} />
          </span>
          <p>{t('taxDecision.warning13b', { scheme: schemeLabel(auto) })}</p>
        </div>
      )}

      <div>
        <label className="block text-faint mb-1" htmlFor="inbound-tax-note">
          {noteRequired
            ? t('taxDecision.noteLabelRequired', 'Begründung der Änderung (erforderlich)')
            : t('taxDecision.noteLabelOptional', 'Begründung (optional)')}
        </label>
        <textarea
          id="inbound-tax-note"
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('taxDecision.notePlaceholder', '')}
          className="w-full bg-surface-raised border border-hairline rounded-lg px-3 py-2 text-primary min-h-[64px]"
        />
      </div>

      {/* Disclaimer (J-1) direkt am Submit. */}
      <p className="text-faint text-xs">{t('taxDecision.disclaimer')}</p>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={saving || noteMissing}
        className="inline-flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
        data-testid="inbound-tax-decision-submit"
      >
        {saving && <IconSpinner className="animate-spin" />}
        {saving
          ? t('taxDecision.saving', 'Wird gespeichert …')
          : t('taxDecision.action.submit', 'Kontierung übernehmen')}
      </button>
    </div>
  );
};

export default InboundTaxDecisionPanel;
